<template>
  <div>
    <v-menu v-model="isMenuOpen">
      <template #activator="{ props: menuProps }">
        <slot name="activator" :props="menuProps">
          <v-btn icon="add" variant="flat" color="primary" v-bind="menuProps" density="comfortable" />
        </slot>
      </template>

      <template #default>
        <v-list>
          <v-list-item
            v-if="!hideAssets"
            prepend-icon="add"
            :title="$t('assets.asset')"
            :disabled="!hasOrganizationWithScope(AuthScope.CAN_CREATE_ASSETS)"
            @click="dialogOpen = true"
          />
          <v-list-item
            v-if="!allowedTicketTypes || allowedTicketTypes.includes(TicketType.INSPECTION)"
            prepend-icon="add"
            :title="$t('inspections.inspection')"
            :disabled="!hasOrganizationWithScope(AuthScope.CAN_CREATE_INSPECTIONS)"
            @click="inspectionDialogOpen = true"
          />
          <v-list-item
            v-if="!allowedTicketTypes || allowedTicketTypes.includes(TicketType.NEN3140)"
            prepend-icon="add"
            :title="$t('nen3140s.nen3140')"
            :disabled="!hasOrganizationWithScope(AuthScope.CAN_CREATE_INSPECTIONS)"
            @click="nen3140DialogOpen = true"
          />
          <v-list-item
            v-if="!allowedTicketTypes || allowedTicketTypes.includes(TicketType.NEN1010)"
            prepend-icon="add"
            :title="$t('nen1010s.nen1010')"
            :disabled="!hasOrganizationWithScope(AuthScope.CAN_CREATE_INSPECTIONS)"
            @click="nen1010DialogOpen = true"
          />
          <v-list-item
            v-if="!allowedTicketTypes || allowedTicketTypes.includes(TicketType.MALFUNCTION)"
            prepend-icon="add"
            :title="$t('malfunctions.malfunction')"
            :disabled="!hasOrganizationWithScope(AuthScope.CAN_CREATE_MALFUNCTIONS)"
            @click="malfunctionDialogOpen = true"
          />
          <v-list-item
            v-if="!allowedTicketTypes || allowedTicketTypes.includes(TicketType.ACTION)"
            prepend-icon="add"
            :title="$t('actions.action')"
            :disabled="!hasOrganizationWithScope(AuthScope.CAN_CREATE_ACTIONS)"
            @click="actionDialogOpen = true"
          />
        </v-list>
      </template>
    </v-menu>

    <v-dialog v-model="dialogOpen" width="1000" persistent :disabled="!hasOrganizationWithScope(AuthScope.CAN_CREATE_ASSETS)">
      <template #default="{ isActive }">
        <asset-form
          @ok="
            (asset: Asset) => {
              dialogOpen = false
              isActive.value = false
              emit('ok')
              $router.push(`/assets/${asset._id}`)
            }
          "
          @cancel="
            () => {
              isActive.value = false
              emit('ok')
            }
          "
        />
      </template>
    </v-dialog>

    <v-dialog
      v-if="!allowedTicketTypes || allowedTicketTypes.includes(TicketType.INSPECTION)"
      v-model="inspectionDialogOpen"
      width="1000"
      persistent
      :disabled="!hasOrganizationWithScope(AuthScope.CAN_CREATE_INSPECTIONS)"
    >
      <template #default="{ isActive }">
        <dialog-ticket
          :ticket-type="TicketType.INSPECTION"
          :asset-id="assetId"
          :organization-id="organizationId"
          @ok="
            () => {
              isActive.value = false
              emit('ok')
            }
          "
          @cancel="
            () => {
              isActive.value = false
              emit('ok')
            }
          "
        />
      </template>
    </v-dialog>
    <v-dialog v-model="nen3140DialogOpen" width="1000" persistent :disabled="!hasOrganizationWithScope(AuthScope.CAN_CREATE_INSPECTIONS)">
      <template #default="{ isActive }">
        <dialog-ticket
          :ticket-type="TicketType.NEN3140"
          :asset-id="assetId"
          :organization-id="organizationId"
          @ok="
            () => {
              isActive.value = false
              emit('ok')
            }
          "
          @cancel="
            () => {
              isActive.value = false
              emit('ok')
            }
          "
        />
      </template>
    </v-dialog>
    <v-dialog v-model="nen1010DialogOpen" width="1000" persistent :disabled="!hasOrganizationWithScope(AuthScope.CAN_CREATE_INSPECTIONS)">
      <template #default="{ isActive }">
        <dialog-ticket
          :ticket-type="TicketType.NEN1010"
          :asset-id="assetId"
          :organization-id="organizationId"
          @ok="
            () => {
              isActive.value = false
              emit('ok')
            }
          "
          @cancel="
            () => {
              isActive.value = false
              emit('ok')
            }
          "
        />
      </template>
    </v-dialog>
    <v-dialog
      v-model="malfunctionDialogOpen"
      width="1000"
      persistent
      :disabled="!hasOrganizationWithScope(AuthScope.CAN_CREATE_MALFUNCTIONS)"
    >
      <template #default="{ isActive }">
        <dialog-ticket
          :ticket-type="TicketType.MALFUNCTION"
          :asset-id="assetId"
          :organization-id="organizationId"
          @ok="
            () => {
              isActive.value = false
              emit('ok')
            }
          "
          @cancel="
            () => {
              isActive.value = false
              emit('ok')
            }
          "
        />
      </template>
    </v-dialog>
    <v-dialog v-model="actionDialogOpen" width="1000" :disabled="!hasOrganizationWithScope(AuthScope.CAN_CREATE_ACTIONS)">
      <template #default="{ isActive }">
        <dialog-ticket
          :ticket-type="TicketType.ACTION"
          :asset-id="assetId"
          :organization-id="organizationId"
          @ok="
            () => {
              isActive.value = false
              emit('ok')
            }
          "
          @cancel="
            () => {
              isActive.value = false
              emit('ok')
            }
          "
        />
      </template>
    </v-dialog>

    <Teleport v-if="isMenuOpen && !hideOverlay" to="#add-menu-backdrop-target">
      <div v-if="!isAnyDialogOpen" class="menu-backdrop" @click="isMenuOpen = false" />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
const $router = useRouter()

const props = defineProps<{
  hideAssets?: boolean
  allowedTicketTypes?: Array<TicketType>
  assetId?: string
  hideOverlay?: boolean
  organizationId?: string
}>()

const { hideAssets, allowedTicketTypes, assetId, organizationId } = toRefs(props)

const { hasOrganizationWithScope } = useAuthStore()

const emit = defineEmits<{
  (e: "ok"): void
}>()

const isMenuOpen = ref(false)

const dialogOpen = ref(false)
const inspectionDialogOpen = ref(false)
const nen3140DialogOpen = ref(false)
const nen1010DialogOpen = ref(false)
const malfunctionDialogOpen = ref(false)
const actionDialogOpen = ref(false)

const isAnyDialogOpen = computed(
  () =>
    dialogOpen.value ||
    inspectionDialogOpen.value ||
    nen3140DialogOpen.value ||
    nen1010DialogOpen.value ||
    malfunctionDialogOpen.value ||
    actionDialogOpen.value,
)
</script>
